import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Button, FormGroup, Label, Input,
} from 'reactstrap';
import Swal from 'sweetalert2';
import {
    createBillingSetupIntent,
    setDefaultPaymentMethod,
    listPaymentMethods,
    billingActionReset,
} from 'redux/Billing/BillingActions';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const CardForm = ({ onDone, makeDefault, setMakeDefault, closing }) => {
    const stripe = useStripe();
    const elements = useElements();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!stripe || !elements) {
            setError('Payment system is still loading. Please wait.');
            return;
        }
        setLoading(true);

        try {
            const { error: submitError } = await elements.submit();
            if (submitError) {
                setError(submitError.message);
                setLoading(false);
                return;
            }

            // Create SetupIntent on backend
            const data = await dispatch(createBillingSetupIntent());
            const { clientSecret } = data;

            const { error: confirmError, setupIntent } = await stripe.confirmSetup({
                elements,
                clientSecret,
                confirmParams: {
                    return_url: window.location.origin + '/client/billing',
                },
                redirect: 'if_required',
            });

            if (confirmError) {
                setError(confirmError.message);
                setLoading(false);
                return;
            }

            if (makeDefault && setupIntent?.payment_method) {
                try {
                    await dispatch(setDefaultPaymentMethod(setupIntent.payment_method));
                } catch (defErr) {
                    console.error('Failed to set default:', defErr);
                }
            }

            // Refresh cards list
            await dispatch(listPaymentMethods());
            dispatch(billingActionReset());
            onDone();
        } catch (err) {
            console.error('Add card error:', err);
            setError(err.response?.data?.error || err.message || 'Failed to add card.');
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <ModalBody>
                <PaymentElement />
                <FormGroup check className="mt-3">
                    <Label check>
                        <Input
                            type="checkbox"
                            checked={makeDefault}
                            onChange={(e) => setMakeDefault(e.target.checked)}
                        />{' '}
                        Set as default payment method
                    </Label>
                </FormGroup>
                {error && <div className="text-danger mt-2 small">{error}</div>}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" type="button" onClick={closing} disabled={loading}>Cancel</Button>
                <Button color="primary" type="submit" disabled={loading || !stripe}>
                    {loading ? 'Adding...' : 'Add Card'}
                </Button>
            </ModalFooter>
        </form>
    );
};

const AddCardModal = ({ isOpen, toggle, onAdded }) => {
    const [makeDefault, setMakeDefault] = useState(true);

    useEffect(() => {
        if (isOpen) setMakeDefault(true);
    }, [isOpen]);

    const handleDone = () => {
        Swal.fire({ title: 'Card Added', text: 'Your new payment method has been saved.', icon: 'success', timer: 2000 });
        if (onAdded) onAdded();
        toggle();
    };

    const options = {
        mode: 'setup',
        currency: 'usd',
        payment_method_types: ['card', 'link'],
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="md">
            <ModalHeader toggle={toggle}>Add a New Card</ModalHeader>
            {isOpen && (
                <Elements stripe={stripePromise} options={options}>
                    <CardForm
                        onDone={handleDone}
                        makeDefault={makeDefault}
                        setMakeDefault={setMakeDefault}
                        closing={toggle}
                    />
                </Elements>
            )}
        </Modal>
    );
};

export default AddCardModal;
